import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Save, Play, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';

const CUTOFF_ACTIONS = [
  { value: 'trim_and_approve', label: 'Trim to the cap and approve' },
  { value: 'approve_in_full', label: 'Approve in full' },
  { value: 'leave_held', label: 'Leave it held' },
];

const FIELDS = [
  {
    key: 'cap_allowance_pct',
    label: 'Allowance',
    suffix: '%',
    min: 100, max: 300,
    hint: 'The ceiling is a client\'s best single delivery times this. 120% means their best day plus a fifth.',
  },
  {
    key: 'min_deliveries_for_cap',
    label: 'Deliveries before a product has a ceiling',
    min: 1, max: 20,
    hint: 'A peak drawn from one day is not a peak. Below this, the product is never gated.',
  },
  {
    key: 'lookback_months',
    label: 'Lookback',
    suffix: 'months',
    min: 1, max: 24,
    hint: 'How far back the best day is read from.',
  },
  {
    key: 'breach_months_to_gate',
    label: 'Breach months that switch approval on',
    min: 1, max: 12,
    hint: 'Consecutive complete months under the watch level.',
  },
  {
    key: 'clean_months_to_release',
    label: 'Clean months that switch it off',
    min: 1, max: 12,
    hint: 'Consecutive complete months at or above the watch level.',
  },
];

/**
 * The policy behind the ceiling and the gate. Every number is editable, so the
 * bakery can tighten or loosen the whole thing without a redeploy.
 */
export const PolicyTab = ({ active = true }) => {
  const [server, setServer] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState('');

  useEffect(() => {
    if (!active) return;
    (async () => {
      try {
        const data = await adminService.getOverOrderSettings();
        setServer(data);
        setForm(data);
      } catch {
        toast.error('Failed to load the over-ordering policy');
      } finally {
        setLoading(false);
      }
    })();
  }, [active]);

  const isDirty = useMemo(() => {
    if (!server || !form) return false;
    return ['cap_allowance_pct', 'min_deliveries_for_cap', 'lookback_months',
      'breach_months_to_gate', 'clean_months_to_release']
      .some((k) => Number(form[k]) !== Number(server[k]))
      || form.cutoff_action !== server.cutoff_action
      || form.warnings_enabled !== server.warnings_enabled;
  }, [server, form]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        cap_allowance_pct: Number(form.cap_allowance_pct),
        min_deliveries_for_cap: Number(form.min_deliveries_for_cap),
        lookback_months: Number(form.lookback_months),
        breach_months_to_gate: Number(form.breach_months_to_gate),
        clean_months_to_release: Number(form.clean_months_to_release),
        cutoff_action: form.cutoff_action,
        warnings_enabled: !!form.warnings_enabled,
      };
      const data = await adminService.updateOverOrderSettings(payload);
      setServer(data);
      setForm(data);
      toast.success('Policy saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save the policy');
    } finally {
      setSaving(false);
    }
  };

  const runJob = async (job) => {
    setRunning(job);
    try {
      const res = job === 'gating'
        ? await adminService.runOverOrderGating()
        : await adminService.runOverOrderReleases();
      if (job === 'gating') {
        toast.success(
          `Judged ${res.clients_evaluated} client${res.clients_evaluated === 1 ? '' : 's'} — `
          + `${res.switched_on?.length || 0} switched on, ${res.switched_off?.length || 0} off`,
        );
      } else {
        toast.success(
          `${res.due} due · ${res.trimmed_and_approved} trimmed, ${res.approved_in_full} approved in full, `
          + `${res.left_held} left held`,
        );
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'The job failed');
    } finally {
      setRunning('');
    }
  };

  if (loading || !form) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="oo-policy-loading">
        <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="oo-policy-tab">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">The ceiling, and when the gate moves</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid gap-5 sm:grid-cols-2">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <Label className="text-sm font-medium text-gray-900">
                  {f.label}{f.suffix ? ` (${f.suffix})` : ''}
                </Label>
                <Input
                  type="number"
                  min={f.min}
                  max={f.max}
                  value={form[f.key] ?? ''}
                  onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                  className="mt-1 w-32 text-sm"
                  data-testid={`oo-policy-${f.key}`}
                />
                <p className="text-xs text-gray-500 mt-1 max-w-md">{f.hint}</p>
              </div>
            ))}
          </div>

          <div className="pt-4 border-t">
            <Label className="text-sm font-medium text-gray-900">
              When the ordering cutoff arrives and nobody has decided
            </Label>
            <Select
              value={form.cutoff_action}
              onValueChange={(v) => setForm((s) => ({ ...s, cutoff_action: v }))}
            >
              <SelectTrigger className="mt-1 max-w-sm" data-testid="oo-policy-cutoff-action">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CUTOFF_ACTIONS.map((a) => (
                  <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-gray-500 mt-1 max-w-lg">
              The default lands the order and leaves the loss behind. Leaving it held means
              somebody must act, and an unattended order will miss its delivery.
            </p>
          </div>

          <div className="flex items-start justify-between gap-6 pt-4 border-t">
            <div>
              <Label className="text-sm font-medium text-gray-900">Show the warning at checkout</Label>
              <p className="text-xs text-gray-500 mt-0.5 max-w-lg">
                The ceiling on each product, on the ordering screen and at checkout. Switching
                this off hides the figures but keeps the hold — a client whose gate is on is still
                asked for a reason, because a silent gate produces phone calls.
              </p>
            </div>
            <Switch
              checked={!!form.warnings_enabled}
              onCheckedChange={(v) => setForm((s) => ({ ...s, warnings_enabled: v }))}
              data-testid="oo-policy-warnings-toggle"
            />
          </div>

          <div className="flex items-center justify-between gap-4 pt-4 border-t">
            <p className="text-xs text-gray-500">
              {server?.updated_by_email
                ? `Last changed by ${server.updated_by_email}`
                : 'Never changed — these are the defaults.'}
            </p>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              onClick={save}
              disabled={saving || !isDirty}
              data-testid="oo-policy-save-btn"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save policy'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Run it now</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-2 text-xs text-gray-500">
            <Info className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" />
            <p>
              Both of these run on their own each day. Triggering one by hand is for when you have
              just changed the policy and want to see it applied.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => runJob('gating')}
              disabled={!!running}
              data-testid="oo-run-gating-btn"
            >
              <Play className="h-4 w-4 mr-2" />
              {running === 'gating' ? 'Judging...' : 'Judge the months'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => runJob('releases')}
              disabled={!!running}
              data-testid="oo-run-releases-btn"
            >
              <Play className="h-4 w-4 mr-2" />
              {running === 'releases' ? 'Releasing...' : 'Settle approvals at the cutoff'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PolicyTab;
